
import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, Loader2, Pencil, ArrowLeft, Camera, XCircle, Upload, UserCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useTranslation } from "react-i18next";

interface EditProfileDialogProps {
  user: {
    id: string;
    email: string;
    name: string | null;
    username: string | null;
    avatar_url: string | null;
  };
  onProfileUpdate: () => void;
}

const EditProfileDialog = ({ user, onProfileUpdate }: EditProfileDialogProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState(user.name || "");
  const [username, setUsername] = useState(user.username || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [selectedAvatar, setSelectedAvatar] = useState<string>(user.avatar_url || "");
  const [isLoading, setIsLoading] = useState(false);
  const [avatars, setAvatars] = useState<string[]>([]);
  const [avatarTab, setAvatarTab] = useState<"preset" | "upload">("preset");
  const [customAvatarFile, setCustomAvatarFile] = useState<File | null>(null);
  const [customAvatarPreview, setCustomAvatarPreview] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!username && user.email) {
      setUsername(user.email.split('@')[0]);
    }
  }, [username, user.email]);

  useEffect(() => {
    if (!name && username) {
      setName(username);
    }
  }, [name, username]);

  const fetchAvatars = async () => {
    try {
      const { data: filesData, error: listError } = await supabase
        .storage
        .from('Avatars')
        .list();

      if (listError) throw listError;

      if (!filesData) return;

      const imageFiles = filesData.filter(file => 
        file.name.match(/\.(jpg|jpeg|png|gif)$/i)
      );

      const avatarUrls = await Promise.all(
        imageFiles.map(async (file) => {
          const { data } = supabase
            .storage
            .from('Avatars')
            .getPublicUrl(file.name);
          
          return data.publicUrl;
        })
      );

      setAvatars(avatarUrls);
      
      if (user.avatar_url) {
        if (avatarUrls.includes(user.avatar_url)) {
          setAvatarTab("preset");
          setSelectedAvatar(user.avatar_url);
        } else {
          setAvatarTab("upload");
          setCustomAvatarPreview(user.avatar_url);
        }
      }
    } catch (error) {
      console.error('Error fetching avatars:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      toast({
        title: t("errors.error", "Error"),
        description: t("profile.fileSizeTooLarge", "File size too large. Please upload an image smaller than 25MB."),
        variant: "destructive",
      });
      return;
    }

    if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/i)) {
      toast({
        title: t("errors.error", "Error"),
        description: t("profile.invalidFileType", "Invalid file type. Please upload an image file (JPEG, PNG, GIF, WEBP)."),
        variant: "destructive",
      });
      return;
    }

    setCustomAvatarFile(file);
    const previewUrl = URL.createObjectURL(file);
    setCustomAvatarPreview(previewUrl);
  };

  const clearCustomAvatar = () => {
    if (customAvatarPreview) {
      if (customAvatarPreview.startsWith('blob:')) {
        URL.revokeObjectURL(customAvatarPreview);
      }
    }
    setCustomAvatarFile(null);
    setCustomAvatarPreview("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadCustomAvatar = async (): Promise<string | null> => {
    if (!customAvatarFile) return null;
    
    setIsUploading(true);
    try {
      console.log("Starting upload for file:", customAvatarFile.name, "Size:", customAvatarFile.size);
      const filePath = `${user.id}/${Date.now()}-${customAvatarFile.name}`;
      
      const { data, error } = await supabase.storage
        .from('user_avatars')
        .upload(filePath, customAvatarFile, {
          cacheControl: '3600',
          upsert: true,
        });
      
      if (error) {
        console.error('Detailed upload error:', error);
        throw new Error(`Upload failed: ${error.message}`);
      }
      
      console.log("Upload successful, getting public URL");
      const { data: urlData } = supabase.storage
        .from('user_avatars')
        .getPublicUrl(filePath);
      
      return urlData.publicUrl;
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      
      // Provide more specific error message based on the error
      let errorMessage = "Failed to upload profile picture. ";
      
      if (error.message?.includes('storage/bucket_not_found')) {
        errorMessage += "The storage bucket doesn't exist.";
      } else if (error.message?.includes('permission')) {
        errorMessage += "Permission denied to upload file.";
      } else if (error.message?.includes('network')) {
        errorMessage += "Check your internet connection and try again.";
      } else {
        errorMessage += "Please try again.";
      }
      
      toast({
        title: t("errors.error", "Error"),
        description: errorMessage,
        variant: "destructive",
      });
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const validateProfileData = () => {
    if (!name.trim()) {
      toast({
        title: t("errors.validation", "Validation Error"),
        description: t("profile.nameRequired", "Name is required"),
        variant: "destructive",
      });
      return false;
    }

    if (!username.trim()) {
      toast({
        title: t("errors.validation", "Validation Error"),
        description: t("profile.usernameRequired", "Username is required"),
        variant: "destructive",
      });
      return false;
    }

    if ((password || confirmPassword) && password !== confirmPassword) {
      toast({
        title: t("errors.validation", "Validation Error"),
        description: t("profile.passwordMismatch", "Passwords do not match"),
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleUpdateProfile = async () => {
    try {
      if (!validateProfileData()) return;
      
      setIsLoading(true);

      if (password || confirmPassword) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: password
        });

        if (passwordError) throw passwordError;
      }

      let avatarUrl = selectedAvatar;
      
      if (avatarTab === "upload") {
        if (customAvatarFile) {
          console.log("Attempting to upload custom avatar");
          const uploadedUrl = await uploadCustomAvatar();
          if (uploadedUrl) {
            console.log("Upload successful, URL:", uploadedUrl);
            avatarUrl = uploadedUrl;
          } else {
            console.error("Upload failed, no URL returned");
            return; // Stop the profile update if the upload failed
          }
        } else if (customAvatarPreview) {
          avatarUrl = customAvatarPreview;
        }
      }

      console.log("Updating profile with avatar URL:", avatarUrl);
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          name,
          username,
          avatar_url: avatarUrl,
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      const profileData = {
        name,
        username,
        avatar_url: avatarUrl,
      };
      localStorage.setItem('user-profile', JSON.stringify(profileData));
      localStorage.setItem('user-profile-time', Date.now().toString());

      toast({
        title: t("profile.success", "Success"),
        description: t("profile.updateSuccess", "Profile updated successfully"),
      });

      onProfileUpdate();
      setIsOpen(false);
    } catch (error: any) {
      console.error("Profile update error:", error);
      toast({
        title: t("errors.error", "Error"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start"
          onClick={() => {
            fetchAvatars();
          }}
        >
          <Pencil className="mr-2 h-4 w-4" />
          {t("profile.edit", "Edit Profile")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] h-[90vh] flex flex-col gap-0 p-0">
        <DialogHeader className="p-6 pb-0">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsOpen(false)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <DialogTitle>{t("profile.edit", "Edit Profile")}</DialogTitle>
          </div>
        </DialogHeader>
        <ScrollArea className="flex-1 p-6">
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>{t("profile.avatar", "Avatar")}</Label>
              
              <Tabs 
                value={avatarTab} 
                onValueChange={(value) => setAvatarTab(value as "preset" | "upload")}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="preset">
                    <Check className="mr-2 h-4 w-4" />
                    {t("profile.presetAvatars", "Preset Avatars")}
                  </TabsTrigger>
                  <TabsTrigger value="upload">
                    <Upload className="mr-2 h-4 w-4" />
                    {t("profile.customPhoto", "Custom Photo")}
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="preset" className="mt-4">
                  <div className="h-48 rounded-md border">
                    <ScrollArea className="h-48">
                      <div className="grid grid-cols-4 gap-4 p-4">
                        {avatars.map((avatar) => (
                          <button
                            key={avatar}
                            type="button"
                            onClick={() => setSelectedAvatar(avatar)}
                            className={cn(
                              "relative w-14 h-14 rounded-full overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all border border-gray-300",
                              selectedAvatar === avatar && "ring-2 ring-primary"
                            )}
                          >
                            <div className="w-full h-full flex items-center justify-center">
                              <img
                                src={avatar}
                                alt={t("profile.avatarOption", "Avatar option")}
                                className="w-10 h-10 object-cover"
                              />
                            </div>
                            {selectedAvatar === avatar && (
                              <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                <Check className="w-4 h-4 text-white" />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </TabsContent>
                
                <TabsContent value="upload" className="mt-4">
                  <div className="flex flex-col items-center space-y-4 py-2">
                    {customAvatarPreview ? (
                      <div className="relative w-32 h-32">
                        <Avatar className="w-32 h-32 border-2 border-primary">
                          <AvatarImage src={customAvatarPreview} className="object-cover" />
                          <AvatarFallback>
                            <UserCircle className="w-12 h-12 text-muted-foreground" />
                          </AvatarFallback>
                        </Avatar>
                        
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute -top-2 -right-2 h-8 w-8 rounded-full"
                          onClick={clearCustomAvatar}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div 
                        className="w-32 h-32 rounded-full border-2 border-dashed border-primary/40 flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Camera className="h-10 w-10 text-primary/60 mb-2" />
                        <span className="text-xs text-center text-muted-foreground px-2">
                          {t("profile.clickToUpload", "Click to upload")}
                        </span>
                      </div>
                    )}
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {customAvatarPreview 
                        ? t("profile.changePhoto", "Change Photo") 
                        : t("profile.uploadPhoto", "Upload Photo")}
                    </Button>
                    
                    <p className="text-xs text-center text-muted-foreground">
                      {t("profile.supportedFormats", "Supported formats: JPEG, PNG, GIF, WEBP")}<br />
                      {t("profile.maximumSize", "Maximum size: 3MB")}
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">{t("profile.name", "Name")}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("profile.yourName", "Your name")}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="username">{t("profile.username", "Username")}</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t("profile.yourUsername", "Your username")}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">{t("profile.newPassword", "New Password (optional)")}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("profile.enterNewPassword", "Enter new password")}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t("profile.confirmNewPassword", "Confirm New Password")}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t("profile.confirmNewPassword", "Confirm new password")}
              />
            </div>
          </div>
        </ScrollArea>
        <div className="p-6 border-t">
          <Button 
            className="w-full" 
            onClick={handleUpdateProfile}
            disabled={isLoading || isUploading}
          >
            {(isLoading || isUploading) ? (
              <div className="flex items-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isUploading 
                  ? t("profile.uploading", "Uploading...") 
                  : t("profile.saving", "Saving...")}
              </div>
            ) : (
              t("profile.saveChanges", "Save Changes")
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { EditProfileDialog };
